import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Share2, Sparkles, ArrowRight } from "lucide-react";

export default function FounderPromo() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/founder").then((r) => setData(r.data)).catch((e) => {
      console.warn("[FounderPromo] /founder failed:", e?.message);
    });
  }, []);

  if (!data || !data.promo_active) return null;

  const shareUrl = `${window.location.origin}/register?ref=${data.referral_code}&role=owner`;

  const copy = (val, label) => { navigator.clipboard.writeText(val); toast.success(label); };

  const share = async () => {
    const shareData = {
      title: "SlipShare — Founding 20",
      text: `Rent out your unused boat dock on SlipShare. The first 20 owners get a $${data.bonus_amount} bonus. Use my code ${data.referral_code}.`,
      url: shareUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (e) {
        // User cancelled or share unsupported — fall through to clipboard copy
        console.debug("[FounderPromo] navigator.share aborted:", e?.name);
      }
    }
    copy(shareUrl, "Share link copied");
  };

  const pct = Math.round((data.spots_claimed / data.spots_total) * 100);

  return (
    <section data-testid="founder-promo" className="container-editorial mt-32">
      <div className="relative overflow-hidden rounded-3xl border border-primary/20 grain">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-[#1a2a23]" />
        <div className="relative grid md:grid-cols-5 gap-10 p-8 sm:p-12 lg:p-16 text-primary-foreground">
          {/* Left: pitch */}
          <div className="md:col-span-3">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#F1C6B5] mb-6">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2}/> The Founding 20
            </div>
            <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
              List your dock first.<br/>
              <span className="italic text-[#F1C6B5]">Earn a ${data.bonus_amount} bonus</span> on us.
            </h2>
            <p className="mt-6 text-white/85 max-w-xl leading-relaxed">
              We're seeding SlipShare with 20 hand-picked dock owners on each lake. Publish a listing with the founder's code
              and we credit your account <strong className="text-white">${data.bonus_amount} the moment you go live</strong> — on top of the 5% you'll earn from every referral.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                data-testid="founder-cta-btn"
                size="lg"
                className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground gap-2 px-6"
                onClick={() => navigate(`/register?ref=${data.referral_code}&role=owner`)}
              >
                Claim my spot <ArrowRight className="h-4 w-4" strokeWidth={2}/>
              </Button>
              <Button
                data-testid="founder-share-btn"
                size="lg"
                variant="outline"
                className="rounded-full bg-transparent border-white/30 text-white hover:bg-white/10 gap-2 px-6"
                onClick={share}
              >
                <Share2 className="h-4 w-4" strokeWidth={2}/> Share with a neighbor
              </Button>
            </div>
          </div>

          {/* Right: code card */}
          <div className="md:col-span-2">
            <div className="rounded-2xl border border-white/15 bg-white/[0.06] backdrop-blur-xl p-6">
              <div className="text-xs uppercase tracking-widest text-[#F1C6B5]">Use founder code</div>
              <div
                data-testid="founder-code"
                className="font-mono text-3xl mt-2 tracking-wide select-all cursor-pointer"
                onClick={() => copy(data.referral_code, "Code copied")}
                title="Click to copy"
              >
                {data.referral_code}
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  data-testid="copy-founder-code-btn"
                  variant="outline"
                  size="sm"
                  className="bg-transparent border-white/30 text-white hover:bg-white/10 gap-1"
                  onClick={() => copy(data.referral_code, "Code copied")}
                >
                  <Copy className="h-3.5 w-3.5"/> Code
                </Button>
                <Button
                  data-testid="copy-founder-link-btn"
                  variant="outline"
                  size="sm"
                  className="bg-transparent border-white/30 text-white hover:bg-white/10 gap-1"
                  onClick={() => copy(shareUrl, "Signup link copied")}
                >
                  <Copy className="h-3.5 w-3.5"/> Link
                </Button>
              </div>

              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs uppercase tracking-widest text-[#F1C6B5]">Spots left</span>
                  <span className="font-display text-3xl" data-testid="spots-remaining">
                    {data.spots_remaining}<span className="text-sm text-white/60"> / {data.spots_total}</span>
                  </span>
                </div>
                <div className="mt-3 h-1 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-accent transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
                <p className="mt-3 text-xs text-white/70">
                  Bonus credits are auto-applied at your first booking. Post your slip to lock in.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
